/* eslint-disable modernloop/restrict-imports.cjs */

/* *
 * @interface AtsSevice
 * Interface for ATS services any new ATS service should implement this interface.
 * Provide the implementation for constants & methods.
 * NOTE: Nothing should be optional here.
 * Imp: Never have atsType set in ATS service
 * */
import { FeatureFlagBool } from '@modernloop/shared/feature-flag';

import { Props as AshbyCardProps } from 'src/views-new/Settings/Integrations/Cards/AshbyCard';
import { Props as GemCardProps } from 'src/views-new/Settings/Integrations/Cards/GemCard';
import { Props as GreenhouseOAuthCardProps } from 'src/views-new/Settings/Integrations/Cards/GreenhouseOAuthCard';
import { Props as KomboCardProps } from 'src/views-new/Settings/Integrations/Cards/KomboCard';
import { Props as LeverCardProps } from 'src/views-new/Settings/Integrations/Cards/LeverCard';
import { Props as WorkdayCardProps } from 'src/views-new/Settings/Integrations/Cards/WorkdayCard';

import { CreateCandidatePermissionDialogProps } from 'src/views-new/JobDetailsPage/SourcingLinks/AtsCreateCandidatePermissionDialogs/types';

import { ListIntegrationResponse } from '../api/integration/useIntegrations';

export type TScorecardSortKey = 'index' | 'groupId' | null;
export type TScorecardGroupByKey = 'groupName' | null;

/**
 * Strategy used by scorecard dropdowns to derive section headers for ATS interview definitions.
 */
export enum ScorecardGroupByStrategy {
  AtsField = 'ATS_FIELD',
  AtsJobStage = 'ATS_JOB_STAGE',
  AshbyInterviewScope = 'ASHBY_INTERVIEW_SCOPE',
}

/**
 * Pages that are supported to be parsed and shown in the extension sidebar.
 */
export enum ExtensionSidebarParsedUrlType {
  Candidate = 'CANDIDATE',
  Unknown = 'UNKNOWN',
}

/**
 * Naming it as refrence as its not a candidate id but a refrence id.
 */
export type ParsedAtsUrlResult = {
  type: ExtensionSidebarParsedUrlType;
  atsCandidateId: string | number | null;
  atsApplicationId: string | number | null;
  atsJobId?: string | null;
  isRefrenceId?: boolean; // If the id is a refrence id currently workday specific
};

/**
 * Integration card props for all the ATS Integration cards.
 * The component should always dictate the props that it needs.
 */
export type IntegrationCardProps =
  | GreenhouseOAuthCardProps
  | LeverCardProps
  | AshbyCardProps
  | GemCardProps
  | KomboCardProps
  | WorkdayCardProps;

interface AtsSevice {
  /* *
   * ATS hostname to be used for parsing the url
   *  */
  hostname: string;

  /*
   * featureFlag is used to check if the ATS is launched or not.
   */
  featureFlag: FeatureFlagBool | null;

  /* *
   * ATS name to be shown, convert snake case to capitalize
   *  */
  name: string;

  /* *
   * ATS logo to be shown, path to the logo /public/static/images/integrations/${atsName}.png
   * */
  logoUrl: string;

  /* *
   * Help center link to be shown for the ATS
   * */
  helpCenterUrl: string;

  /* *
   * Translation key for scorecard labels. Some ATSs call scorecards by product-specific names.
   * */
  scorecardLabelPluralTranslationKey: string;

  /* *
   * Translation key for the empty scorecard option label.
   * */
  noScorecardLabelTranslationKey: string;

  /* *
   * Translation key for the scorecard selection dialog title.
   * */
  scorecardSelectTitleTranslationKey: string;

  /* *
   * Translation key for the scorecard search placeholder.
   * */
  scorecardSearchPlaceholderTranslationKey: string;

  /* *
   * Translation key for the scorecard popover search placeholder.
   * */
  scorecardSearchPlaceholderEllipsisTranslationKey: string;

  /* *
   * If ATS user select is enabled, This allows assigning an user to another user.
   * Currently enable only Lyra Org which is on Lever & managed via FF at or level.
   * This feature is enabled when an org has multiple directory domain users.
   * There is no ATS specific limitation at BE though to enable this feature.
   * */
  isAtsUserSelectEnabled: boolean;

  /* *
   * All email content token validation is done at FE to validate email content has no unreplaced token before sending it to BE
   * As token replacement it done at FE.
   * This is done at FE to avoid sending invalid email content to BE & then getting an error from BE.
   * For Lever , Ashby & Gem currently interviewKitLink token is generated at BE later.
   * So we need skip to validation for interviewKitLink token for specific ATS.
   * */
  skipInterviewKitLinkTokenValidation: boolean;

  /* *
   * Candidate availablity can be imported from ATS to Mloop
   * Currently enabled only for Greenhouse ATS
   * */
  isAtsImportAvailabilityEnabled: boolean;

  /* *
   * To enable sourcing links,  Mloop should have api to create candidate in ATS
   * Currently GEM has no such api to create candidate in ATS. So sourcing link is disabled for GEM
   * */
  isSourcingLinkEnabled: boolean;

  /* *
   *  Scored card selects have sorting around different keys like index, groupId, etc
   *  Eg Lever is groupId
   * */
  scorecardSortKey: TScorecardSortKey;

  /**
   * How scorecard selects group available scorecard options.
   */
  scorecardGroupByStrategy: ScorecardGroupByStrategy;

  /* *
   *  Scored card selects have grouping around different keys like groupName
   *  Eg Lever is groupName
   * */
  scorecardGroupByKey: TScorecardGroupByKey;

  /* *
   *  Scored card selects have estimated minutes to show or not
   *  Eg Greenhouse has estimated minutes to show
   * */
  showEstimatedMinutes: boolean;

  /* *
   * Somewhere in 2024 we started with sourcing links feature.
   * For this feature we required to access create candidate api of ATS.
   * So for all the prior customers we need to take permissions to access create candidate api.
   * So we had to created a dialog to enable permissions for all the prior customers.
   * Currenlty for Greenhouse , Lever & Ashby we have enabled this dialog.
   * If a new ats is added in future we will not need permission as it will be taken upfront.
   * Workday being a new ATS we have not enabled this dialog, as the permission is taken upfront.
   * */
  createCandidatePermissionDialog: React.ComponentType<CreateCandidatePermissionDialogProps> | null;

  /* *
   *  If ATS has requisition ID, this will be used to check whether to show requisition ID on job details page.  Currently applies to Gem and Greenhouse.
   * */
  showRequisitionId: boolean;

  /**
   * Whether the ATS can support Brighthire interview guides in the interview plan and while scheduling.
   */
  canSupportBrightHire: boolean;

  /**
   * Whether the ATS can keep interview-plan scorecards in sync from the ATS job stage sync.
   */
  hasScorecardSync: boolean;

  /**
   * Label for the scorecard synchronization setting in the ATS's terminology.
   */
  scorecardSyncSettingLabelTranslationKey: string;

  /**
   * Parses the extension sidebar URL and returns the parsed result from ATS url & extra data passed via chrome extension.
   */
  parseExtensionSidebarUrl: (input: string, extraData?: string) => ParsedAtsUrlResult | null;

  /**
   * Integration card to be shown in the integrations page
   */
  getIntegrationCard: (
    integrationCardProps: IntegrationCardProps,
    integrations?: ListIntegrationResponse
  ) => JSX.Element;

  /**
   * Returns whether the ATS has hiring processes or not.(Currently only SmartRecruiters has the concept of hiring processes)
   */
  hasHiringProcess: boolean;

  /**
   * Smart recruiters has no concept of scorecards.
   */
  hasScorecards: boolean;

  /**
   * Warning message to be shown when resetting the interview plan.
   */
  resetInterviewPlanWarningMessage: string;

  /**
   * Whether the ATS has enforced job permissions or not.
   */
  hasEnforcedJobPermissions: boolean;
}

export default AtsSevice;
